import { transitionAutorisee } from '@artosera/shared';
import { Button, Cartel, Dialog, Input, Price, StatusDot, Textarea } from '@artosera/ui';
import { ArrowLeft, Image as ImageIcon, TriangleAlert, UserRound, X } from 'lucide-react';
import type { FormEvent, JSX } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useContacts } from '../../api/contacts.js';
import { useOeuvre } from '../../api/oeuvres.js';
import { useApercuMedia } from '../../api/medias.js';
import { galerieALaction, galerieAModule, galerieEcritModule } from '../../auth/sujet.js';
import { couleurDuStatut } from '../oeuvres/statutUi.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import { useShellContext } from '../shell/context.js';
import type {
  CibleContactFoire,
  CorpsOffreFoire,
  CorpsReservationFoire,
  CorpsVenteFoire,
  CorpsVenteSimpleFoire,
} from './useFileHorsLigneFoire.js';
import { useFileHorsLigneFoire } from './useFileHorsLigneFoire.js';
import { lireFileHorsLigne } from './fileHorsLigne.js';
import { dateOffreParDefaut } from '../offres/NouvelleOffreDialog.js';
import { BoutonCalculette } from '../presentation/CalculetteNegociation.js';
import { SuggestionPrixApprouve } from '../presentation/SuggestionPrixApprouve.js';
import styles from './StandOeuvrePage.module.css';

type Geste = 'reservation' | 'demande' | 'vente' | 'venteSimple' | 'offre' | null;

function aujourdhui(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Fiche œuvre en foire (3.3, D5, S1-c, ARTO-4) : visuel plein écran, cartel,
 * prix, et trois gestes tactiles — Réserver, Noter une demande, et Facturer
 * (module `facturation`) ou, sans lui, Marquer vendue (vente simple du cœur,
 * D21).
 * Réserver et Vendre cherchent d'abord un contact existant dans le carnet ;
 * à défaut, une saisie rapide crée le contact avant la réservation ou la
 * facture (§ 14.6, § 17.4 : les deux exigent un `contactId`, jamais un nom
 * en clair). Chaque geste écrit d'abord dans la file hors ligne (réseau de
 * salon), puis tente le rejeu réseau immédiatement s'il y a un accès.
 */
export function StandOeuvrePage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'oeuvres', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const { galerieSlug, galerie } = useShellContext();
  const { oeuvreId } = useParams<{ oeuvreId: string }>();
  const { empiler, rejouer, stockageMemoire, arretRejeu, actionsEnQuarantaine, actionsAVerifier } =
    useFileHorsLigneFoire(galerieSlug);
  const peutEmettre = galerieALaction(galerie, 'invoices:issue');
  // « Facturer » prépare une facture : module `facturation` (D21). Sans lui,
  // « Marquer vendue » enregistre la vente simple du cœur.
  const venteOuverte = galerieEcritModule(galerie, 'facturation');
  const peutMarquerVendue = galerieALaction(galerie, 'artworks:write');

  const { data, isLoading } = useOeuvre(galerieSlug, oeuvreId ?? null);
  // Images du mode foire (ARTO-53, § 5) : la fiche du stand montre l'œuvre,
  // plus une icône (audit du 23 septembre 2026, § 3).
  const mediaPrincipal =
    data?.medias.find((media) => media.id === data.oeuvre.mediaPrincipalId) ??
    data?.medias[0] ??
    null;
  const apercu = useApercuMedia(galerieSlug, mediaPrincipal?.id ?? null);
  const [gesteOuvert, setGesteOuvert] = useState<Geste>(null);
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [jusquau, setJusquau] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const [rechercheContact, setRechercheContact] = useState('');
  const rechercheContactDebattue = useDebounce(rechercheContact, 300);
  const [contactChoisi, setContactChoisi] = useState<{ id: string; nom: string } | null>(null);
  const [emettreMaintenant, setEmettreMaintenant] = useState(false);
  const [dateEmission, setDateEmission] = useState(aujourdhui);
  const [exemplaireId, setExemplaireId] = useState('');
  const [validiteOffre, setValiditeOffre] = useState(dateOffreParDefaut);
  const [prixVenteSimple, setPrixVenteSimple] = useState('');

  const rechercheContactActive =
    (gesteOuvert === 'reservation' ||
      gesteOuvert === 'vente' ||
      gesteOuvert === 'venteSimple' ||
      gesteOuvert === 'offre') &&
    !contactChoisi &&
    rechercheContactDebattue.length >= 2;
  const { data: resultatsContacts } = useContacts(
    galerieSlug,
    { q: rechercheContactDebattue, parPage: 5 },
    { enabled: rechercheContactActive },
  );

  const cibleContact: CibleContactFoire = useMemo(
    () => (contactChoisi ? { contactId: contactChoisi.id } : { rapide: { nom, email } }),
    [contactChoisi, nom, email],
  );

  if (isLoading || !data) {
    return (
      <p className={styles.chargement}>{t('commun:chargement', { defaultValue: 'Chargement…' })}</p>
    );
  }

  const { oeuvre } = data;
  // Une édition suivie exemplaire par exemplaire ne se vend pas entière : le
  // geste « Vendre » exige le choix d'un exemplaire encore vendable (N7).
  const editionSuivie =
    oeuvre.type === 'edition' &&
    (oeuvre.tirage?.exemplairesSuivis === true || data.editions.length > 0);
  // Vendre ou réserver une édition suivie exige l'exemplaire (N7, C2).
  const exigeExemplaire =
    editionSuivie &&
    (gesteOuvert === 'vente' || gesteOuvert === 'reservation' || gesteOuvert === 'offre');
  const exemplaireChoisi = data.editions.find((edition) => edition.id === exemplaireId);
  // Le prix de l'exemplaire choisi, repli sur celui de l'œuvre : c'est celui
  // que le stand affiche, et que la file garde pour le rejeu (constat 7).
  const prixDeVente =
    editionSuivie && exemplaireChoisi !== undefined
      ? (exemplaireChoisi.prixCentimes ?? oeuvre.prixPublicCentimes)
      : oeuvre.prixPublicCentimes;
  const exemplairesVendables = data.editions.filter((edition) =>
    transitionAutorisee(
      edition.statut,
      gesteOuvert === 'reservation' ? 'reservation' : 'facture_emise',
    ),
  );
  // La cible d'une édition suivie est l'exemplaire, pas le statut agrégé
  // de sa mère. Une facture de tirage non suivi consomme une unité du stock.
  const stockFacturable =
    oeuvre.type !== 'edition' ||
    oeuvre.tirage === null ||
    oeuvre.tirage.vendus <
      oeuvre.tirage.taille + oeuvre.tirage.epreuvesArtiste + oeuvre.tirage.epreuvesEssai;
  const reservationPossible = editionSuivie
    ? data.editions.some((edition) => transitionAutorisee(edition.statut, 'reservation'))
    : transitionAutorisee(oeuvre.statut, 'reservation');
  const facturePossible = editionSuivie
    ? data.editions.some((edition) => transitionAutorisee(edition.statut, 'facture_emise'))
    : transitionAutorisee(oeuvre.statut, 'facture_emise') && stockFacturable;
  const venteSimplePossible = !editionSuivie && transitionAutorisee(oeuvre.statut, 'vente_simple');
  const gesteDisponible = (geste: Geste): boolean => {
    if (geste === 'venteSimple') return venteSimplePossible;
    if (geste !== 'reservation' && geste !== 'vente') return true;
    if (editionSuivie) {
      return (
        exemplaireChoisi !== undefined &&
        transitionAutorisee(
          exemplaireChoisi.statut,
          geste === 'reservation' ? 'reservation' : 'facture_emise',
        )
      );
    }
    return geste === 'reservation' ? reservationPossible : facturePossible;
  };

  const reinitialiserFormulaire = (): void => {
    setNom('');
    setEmail('');
    setNote('');
    setJusquau('');
    setRechercheContact('');
    setContactChoisi(null);
    setEmettreMaintenant(false);
    setDateEmission(aujourdhui());
    setExemplaireId('');
    setPrixVenteSimple('');
    setValiditeOffre(dateOffreParDefaut());
  };

  const fermerDialogue = (): void => {
    setGesteOuvert(null);
    reinitialiserFormulaire();
  };

  const soumettreGeste = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!gesteOuvert || !oeuvreId || enCours) return;
    if (exigeExemplaire && !exemplaireId) {
      setMessage(
        t(
          gesteOuvert === 'reservation'
            ? 'regie:foire.fiche.exemplaireAReserver'
            : 'regie:foire.fiche.exemplaireManquant',
        ),
      );
      return;
    }
    // Même un dialogue resté ouvert après un rafraîchissement doit refuser
    // avant empiler/rejouer : le rejeu pourrait créer un contact et un brouillon.
    if (!gesteDisponible(gesteOuvert)) {
      fermerDialogue();
      setMessage(t('regie:foire.fiche.gesteIndisponible'));
      return;
    }
    setEnCours(true);
    // Le message de succès n'est affiché qu'une fois le geste RÉELLEMENT
    // passé : un rejeu refusé par l'API laisse l'action dans la file, et le
    // stand doit le dire au lieu d'annoncer « Facture émise. ».
    let actionId: string | null = null;
    let succes: string;
    try {
      if (gesteOuvert === 'reservation') {
        const corps: CorpsReservationFoire = {
          contact: cibleContact,
          oeuvreId,
          // Une édition suivie se réserve exemplaire par exemplaire (C2).
          ...(editionSuivie && exemplaireId ? { exemplaireId } : {}),
          jusquau: jusquau
            ? new Date(jusquau).toISOString()
            : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          note,
        };
        actionId = empiler('reservation', corps).id;
        succes = t('regie:foire.fiche.succesReservation');
      } else if (gesteOuvert === 'demande') {
        const corps = {
          nom,
          email,
          message: note,
          oeuvreId,
          rattacherAuCarnet: true,
          source: 'regie' as const,
        };
        actionId = empiler('demande', corps).id;
        succes = t('regie:foire.fiche.succesDemande');
      } else if (gesteOuvert === 'offre') {
        const euros = Number(prixVenteSimple.replace(',', '.'));
        if (!validiteOffre || !Number.isFinite(euros) || euros < 0) {
          setMessage(t('regie:foire.fiche.offreInvalide'));
          return;
        }
        const corps: CorpsOffreFoire = {
          contact: cibleContact,
          oeuvreId,
          ...(editionSuivie && exemplaireId ? { exemplaireId } : {}),
          prixProposeCentimes:
            prixVenteSimple.trim() === '' ? (prixDeVente ?? 0) : Math.round(euros * 100),
          validiteJusquau: validiteOffre,
          ...(note ? { message: note } : {}),
        };
        actionId = empiler('offre', corps).id;
        succes = t('regie:foire.fiche.succesOffre');
      } else if (gesteOuvert === 'venteSimple') {
        const euros = Number(prixVenteSimple.replace(',', '.'));
        const corps: CorpsVenteSimpleFoire = {
          contact: cibleContact,
          oeuvreId,
          prixCentimes:
            prixVenteSimple.trim() === '' || !Number.isFinite(euros)
              ? (oeuvre.prixPublicCentimes ?? 0)
              : Math.round(euros * 100),
          // La date du geste, pas celle d'un rejeu tardif.
          venduLe: aujourdhui(),
        };
        actionId = empiler('vente-simple', corps).id;
        succes = t('regie:foire.fiche.succesVenteSimple');
      } else {
        const corps: CorpsVenteFoire = {
          contact: cibleContact,
          lignes: [
            {
              oeuvreId,
              ...(editionSuivie && exemplaireId ? { exemplaireId } : {}),
              designation: oeuvre.titre,
              // Le prix AFFICHÉ au stand — celui de l'exemplaire choisi —,
              // conservé dans la file : le rejeu vend au prix convenu.
              ...(prixDeVente === null ? {} : { prixUnitaireCentimes: prixDeVente }),
            },
          ],
          ...(peutEmettre && emettreMaintenant ? { emettre: { emiseLe: dateEmission } } : {}),
        };
        actionId = empiler('vente', corps).id;
        succes =
          peutEmettre && emettreMaintenant
            ? t('regie:foire.fiche.succesVenteEmise')
            : t('regie:foire.fiche.succesVente');
      }
      await rejouer();
      const resteEnAttente = lireFileHorsLigne().some((action) => action.id === actionId);
      setMessage(
        !resteEnAttente
          ? succes
          : navigator.onLine
            ? t('regie:foire.fiche.echec')
            : t('regie:foire.fiche.enregistreHorsLigne'),
      );
      fermerDialogue();
    } catch {
      setMessage(t('regie:foire.fiche.enregistreHorsLigne'));
    } finally {
      setEnCours(false);
    }
  };

  const geteContactBesoin =
    gesteOuvert === 'reservation' ||
    gesteOuvert === 'vente' ||
    gesteOuvert === 'venteSimple' ||
    gesteOuvert === 'offre';

  const statut = couleurDuStatut(oeuvre.statut);

  return (
    <div className={styles.page}>
      <Link className={styles.retour} to=".." relative="path">
        <ArrowLeft aria-hidden="true" size={18} /> {t('regie:foire.retourStand')}
      </Link>
      {/* La scène : l'œuvre seule sur son pan de cimaise, comme au stand. */}
      <div className={styles.scene} aria-hidden="true">
        {apercu.data ? (
          <img className={styles.oeuvreAccrochee} src={apercu.data} alt="" />
        ) : (
          <span className={styles.cadreVide}>
            <ImageIcon size={48} />
          </span>
        )}
      </div>
      <div className={styles.panneau}>
        <span className={styles.statut} data-statut={statut}>
          <StatusDot status={statut} lang={langue} label={t(`oeuvres:statuts.${oeuvre.statut}`)} />
        </span>
        <Cartel
          className={styles.cartel}
          size="lg"
          artist={oeuvre.artisteNom}
          title={oeuvre.titre}
          year={oeuvre.annee ?? undefined}
          inventoryNumber={oeuvre.numeroInventaire}
        />
        {oeuvre.prixPublicCentimes !== null ? (
          <div className={styles.prixLigne}>
            <Price
              className={styles.prix}
              cents={oeuvre.prixPublicCentimes}
              lang={langue}
              currency={oeuvre.devise}
              size="lg"
            />
          </div>
        ) : null}

        {message ? (
          <p className={styles.avis} role="status">
            {message}
          </p>
        ) : null}
        {actionsEnQuarantaine > 0 ? (
          <p className={styles.avisAlerte} role="alert">
            {t('regie:foire.horsLigne.quarantaine', { count: actionsEnQuarantaine })}
          </p>
        ) : null}
        {actionsAVerifier > 0 ? (
          <p className={styles.avisAlerte} role="alert">
            {t('regie:foire.horsLigne.aVerifier', { count: actionsAVerifier })}
          </p>
        ) : null}
        {arretRejeu ? (
          <p className={styles.avisAlerte} role="alert">
            {t(`regie:foire.horsLigne.arret.${arretRejeu}`)}
          </p>
        ) : null}
        {stockageMemoire ? (
          <p className={styles.avisAlerte} role="alert">
            {t('regie:foire.horsLigne.stockageMemoire')}
          </p>
        ) : null}

        <div className={styles.gestes}>
          <BoutonCalculette
            oeuvreId={oeuvre.id}
            exemplaireId={exemplaireId || null}
            devise={oeuvre.devise}
            contactId={contactChoisi?.id}
          />
          <Button
            size="lg"
            variant="secondary"
            disabled={!reservationPossible || enCours}
            aria-describedby={!reservationPossible ? 'foire-actions-disponibilite' : undefined}
            onClick={() => setGesteOuvert('reservation')}
          >
            {t('regie:foire.fiche.reserver')}
          </Button>
          <Button size="lg" variant="secondary" onClick={() => setGesteOuvert('demande')}>
            {t('regie:foire.fiche.noterDemande')}
          </Button>
          {galerieAModule(galerie, 'offres-relances') &&
          galerieALaction(galerie, 'offers:write') ? (
            <Button
              size="lg"
              variant="secondary"
              onClick={() => {
                setPrixVenteSimple(
                  oeuvre.prixPublicCentimes === null ? '' : String(oeuvre.prixPublicCentimes / 100),
                );
                setGesteOuvert('offre');
              }}
            >
              {t('regie:foire.fiche.faireOffre')}
            </Button>
          ) : null}
          {/* Facturer prépare une facture : module `facturation` (D21). */}
          {venteOuverte ? (
            <Button
              size="lg"
              variant="accent"
              disabled={!facturePossible || enCours}
              aria-describedby={!facturePossible ? 'foire-actions-disponibilite' : undefined}
              onClick={() => setGesteOuvert('vente')}
            >
              {t('regie:foire.fiche.vendre')}
            </Button>
          ) : null}
          {/* La vente simple du cœur, sans facture : l'action principale sans
              facturation, une action SECONDAIRE avec (« Vendre sans facture »).
              Une édition suivie se vend exemplaire par exemplaire, par une
              facture : pas ici. */}
          {peutMarquerVendue && !editionSuivie ? (
            <Button
              size="lg"
              variant={venteOuverte ? 'ghost' : 'accent'}
              disabled={!venteSimplePossible || enCours}
              aria-describedby={!venteSimplePossible ? 'foire-actions-disponibilite' : undefined}
              onClick={() => {
                setPrixVenteSimple(
                  oeuvre.prixPublicCentimes === null ? '' : String(oeuvre.prixPublicCentimes / 100),
                );
                setGesteOuvert('venteSimple');
              }}
            >
              {venteOuverte
                ? t('regie:foire.fiche.vendreSansFacture')
                : t('regie:foire.fiche.marquerVendue')}
            </Button>
          ) : null}
        </div>
        {!reservationPossible ||
        (venteOuverte && !facturePossible) ||
        (peutMarquerVendue && !editionSuivie && !venteSimplePossible) ? (
          <p className={styles.avis} id="foire-actions-disponibilite">
            {t('regie:foire.fiche.actionsDisponibilite')}
          </p>
        ) : null}
      </div>

      <Dialog
        open={gesteOuvert !== null}
        onOpenChange={(ouvert) => !ouvert && fermerDialogue()}
        closeLabel={t('regie:foire.fiche.annuler')}
        title={
          gesteOuvert === 'offre'
            ? t('regie:foire.fiche.offreTitre')
            : gesteOuvert === 'reservation'
              ? t('regie:foire.fiche.reserverTitre', { titre: oeuvre.titre })
              : gesteOuvert === 'demande'
                ? t('regie:foire.fiche.demandeTitre')
                : gesteOuvert === 'venteSimple'
                  ? venteOuverte
                    ? t('regie:foire.fiche.vendreSansFactureTitre', { titre: oeuvre.titre })
                    : t('regie:foire.fiche.marquerVendueTitre', { titre: oeuvre.titre })
                  : t('regie:foire.fiche.vendreTitre')
        }
        description={
          gesteOuvert === 'offre'
            ? t('regie:foire.fiche.offreDescription')
            : gesteOuvert === 'reservation'
              ? t('regie:foire.fiche.reserverDescription')
              : gesteOuvert === 'demande'
                ? t('regie:foire.fiche.demandeDescription')
                : gesteOuvert === 'venteSimple'
                  ? venteOuverte
                    ? t('regie:foire.fiche.vendreSansFactureDescription')
                    : t('regie:foire.fiche.marquerVendueDescription')
                  : peutEmettre && emettreMaintenant
                    ? t('regie:foire.fiche.vendreEmissionDescription')
                    : t('regie:foire.fiche.vendreDescription')
        }
      >
        <form className={styles.formulaire} onSubmit={(event) => void soumettreGeste(event)}>
          {geteContactBesoin ? (
            contactChoisi ? (
              <div className={styles.contactChoisi}>
                <UserRound className={styles.contactIcone} aria-hidden="true" size={20} />
                <span>{t('regie:foire.fiche.contactChoisi', { nom: contactChoisi.nom })}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setContactChoisi(null)}
                >
                  <X aria-hidden="true" size={14} /> {t('regie:foire.fiche.changerContact')}
                </Button>
              </div>
            ) : (
              <div className={styles.champ}>
                <label htmlFor="foire-geste-recherche-contact">
                  {t('regie:foire.fiche.rechercherContact')}
                </label>
                <Input
                  id="foire-geste-recherche-contact"
                  value={rechercheContact}
                  onChange={(e) => setRechercheContact(e.target.value)}
                  placeholder={t('regie:foire.fiche.rechercherContactPlaceholder')}
                />
                {resultatsContacts && resultatsContacts.contacts.length > 0 ? (
                  <ul className={styles.resultatsContacts}>
                    {resultatsContacts.contacts.map((contact) => (
                      <li key={contact.id}>
                        <button
                          type="button"
                          onClick={() =>
                            setContactChoisi({ id: contact.id, nom: contact.nomAffiche })
                          }
                        >
                          <UserRound aria-hidden="true" size={18} />
                          {contact.nomAffiche}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            )
          ) : null}

          {geteContactBesoin && contactChoisi ? null : (
            <>
              <div className={styles.champ}>
                <label htmlFor="foire-geste-nom">{t('regie:foire.fiche.champNom')}</label>
                <Input
                  id="foire-geste-nom"
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  required
                />
              </div>
              <div className={styles.champ}>
                <label htmlFor="foire-geste-email">{t('regie:foire.fiche.champEmail')}</label>
                <Input
                  id="foire-geste-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </>
          )}

          {gesteOuvert === 'reservation' ? (
            <div className={styles.champ}>
              <label htmlFor="foire-geste-jusquau">{t('regie:foire.fiche.champJusquau')}</label>
              <Input
                id="foire-geste-jusquau"
                type="date"
                value={jusquau}
                onChange={(e) => setJusquau(e.target.value)}
              />
            </div>
          ) : null}

          {exigeExemplaire ? (
            <div className={styles.champ}>
              <label htmlFor="foire-geste-exemplaire">
                {t('regie:foire.fiche.champExemplaire')}
              </label>
              <select
                id="foire-geste-exemplaire"
                className={styles.selecteur}
                value={exemplaireId}
                onChange={(e) => setExemplaireId(e.target.value)}
                required
              >
                <option value="">—</option>
                {exemplairesVendables.map((edition) => (
                  <option key={edition.id} value={edition.id}>
                    {edition.numero}
                  </option>
                ))}
              </select>
              {exemplaireChoisi !== undefined && prixDeVente !== null ? (
                <Price
                  className={styles.prixExemplaire}
                  cents={prixDeVente}
                  lang={langue}
                  currency={oeuvre.devise}
                />
              ) : null}
            </div>
          ) : null}

          {gesteOuvert === 'venteSimple' || gesteOuvert === 'offre' ? (
            <div className={styles.champ}>
              <label htmlFor="foire-geste-prix">
                {t(
                  gesteOuvert === 'offre'
                    ? 'regie:foire.fiche.prixPropose'
                    : 'regie:foire.fiche.champPrix',
                )}
              </label>
              <Input
                id="foire-geste-prix"
                inputMode="decimal"
                value={prixVenteSimple}
                onChange={(e) => setPrixVenteSimple(e.target.value)}
                required
              />
              {((gesteOuvert === 'offre' &&
                galerieAModule(galerie, 'offres-relances') &&
                galerieALaction(galerie, 'offers:write')) ||
                (gesteOuvert === 'venteSimple' && peutMarquerVendue)) &&
              (!exigeExemplaire || exemplaireId) ? (
                <SuggestionPrixApprouve
                  contactId={contactChoisi?.id ?? null}
                  oeuvreId={oeuvre.id}
                  exemplaireId={exigeExemplaire ? exemplaireId : null}
                  devise={oeuvre.devise}
                  onAppliquer={(prixCentimes) => setPrixVenteSimple(String(prixCentimes / 100))}
                />
              ) : null}
            </div>
          ) : null}

          {gesteOuvert === 'offre' ? (
            <div className={styles.champ}>
              <label htmlFor="foire-offre-validite">{t('regie:foire.fiche.validite')}</label>
              <Input
                id="foire-offre-validite"
                type="date"
                required
                value={validiteOffre}
                onChange={(e) => setValiditeOffre(e.target.value)}
              />
            </div>
          ) : null}
          {gesteOuvert === 'vente' && peutEmettre ? (
            <div className={styles.champ}>
              <label className={styles.caseEmettre}>
                <input
                  type="checkbox"
                  checked={emettreMaintenant}
                  onChange={(e) => setEmettreMaintenant(e.target.checked)}
                />{' '}
                {t('regie:foire.fiche.emettreMaintenant')}
              </label>
              {emettreMaintenant ? (
                <>
                  <p className={styles.avertissement}>
                    <TriangleAlert aria-hidden="true" size={18} />
                    {t('regie:foire.fiche.emettreConfirmation')}
                  </p>
                  <label htmlFor="foire-geste-date-emission">
                    {t('regie:foire.fiche.champDateEmission')}
                  </label>
                  <Input
                    id="foire-geste-date-emission"
                    type="date"
                    value={dateEmission}
                    onChange={(e) => setDateEmission(e.target.value)}
                    required
                  />
                </>
              ) : null}
            </div>
          ) : null}

          <div className={styles.champ}>
            <label htmlFor="foire-geste-note">{t('regie:foire.fiche.champNote')}</label>
            <Textarea
              id="foire-geste-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <Button
            className={styles.valider}
            type="submit"
            variant="accent"
            size="lg"
            loading={enCours}
          >
            {gesteOuvert === 'offre'
              ? t('regie:foire.fiche.enregistrerOffre')
              : gesteOuvert === 'vente'
                ? peutEmettre && emettreMaintenant
                  ? t('regie:foire.fiche.emettre')
                  : t('regie:foire.fiche.enregistrerBrouillon')
                : gesteOuvert === 'venteSimple'
                  ? venteOuverte
                    ? t('regie:foire.fiche.confirmerSansFacture')
                    : t('regie:foire.fiche.marquerVendue')
                  : t('regie:foire.fiche.valider')}
          </Button>
        </form>
      </Dialog>
    </div>
  );
}
